/*
 * 0.87: Wochen- und Monatsbaustein der Übersicht. Reine Datumsrechnung – Server (Auswahl der Karten) und Browser
 * (Anordnung, Umplanen per Ziehen) rechnen gleich. Kalendertage als „JJJJ-MM-TT“; Termine mit Uhrzeit liegen in
 * der Zeitzone des Rechners (Browser: Betrachter, Server: Instanz) – der Server wählt darum mit einem Tag Puffer.
 */
import { allDayIso, calendarDate, localDateKey, moveToDate, shiftStartWithDue, startAfterDue } from './dates.js';

export const CAL_RANGES = ['week', 'workweek', 'twoWeeks', 'month'] as const;
export type CalRange = (typeof CAL_RANGES)[number];
export const CAL_RANGE_TEXT: Record<CalRange, string> = { week: 'Woche', workweek: 'Arbeitswoche (Mo–Fr)', twoWeeks: 'Zwei Wochen', month: 'Monat' };

/** Welche Termine: Frist, Start, Zeitraum Start–Frist als ein Eintrag, Start und Frist einzeln */
export const CAL_DATES = ['due', 'start', 'span', 'both'] as const;
export type CalDates = (typeof CAL_DATES)[number];
export const CAL_DATES_TEXT: Record<CalDates, string> = {
  due: 'Fristen',
  start: 'Starts',
  span: 'Zeitraum von Start bis Frist',
  both: 'Start und Frist als eigene Einträge',
};

/** Streifen (eine Zeile mit Titel) oder Flächen (Zeitraster mit genauer Uhrzeit bzw. große Kacheln) */
export const CAL_LOOKS = ['strips', 'blocks'] as const;
export type CalLook = (typeof CAL_LOOKS)[number];

export const CAL_SHOW = ['time', 'board', 'column', 'assignees', 'labels', 'priority'] as const;
export type CalShow = (typeof CAL_SHOW)[number];
export const CAL_SHOW_TEXT: Record<CalShow, string> = {
  time: 'Uhrzeit',
  board: 'Board',
  column: 'Spalte',
  assignees: 'Zuständige',
  labels: 'Labels',
  priority: 'Priorität',
};

export const CAL_COLORS = ['status', 'board', 'label', 'priority', 'none'] as const;
export type CalColor = (typeof CAL_COLORS)[number];
export const CAL_COLOR_TEXT: Record<CalColor, string> = {
  status: 'Nach Fristlage (überfällig rot)',
  board: 'Nach Board',
  label: 'Nach erstem Label',
  priority: 'Nach Priorität',
  none: 'Einheitlich',
};

/* ---------- Tage ---------- */

const parse = (k: string) => {
  const [y, m, d] = k.split('-').map(Number);
  return new Date(Date.UTC(y!, m! - 1, d!));
};
const key = (d: Date) => d.toISOString().slice(0, 10);

/** Kalenderdatum + n Tage */
export function addDayKeys(k: string, n: number): string {
  const d = parse(k);
  d.setUTCDate(d.getUTCDate() + n);
  return key(d);
}
/** Tage von a bis b (b − a) */
export function dayDiff(a: string, b: string): number {
  return Math.round((parse(b).getTime() - parse(a).getTime()) / 86_400_000);
}
/** Wochentag 0 = Montag … 6 = Sonntag */
export const weekdayOf = (k: string) => (parse(k).getUTCDay() + 6) % 7;
const mondayOf = (k: string) => addDayKeys(k, -weekdayOf(k));

/**
 * Sichtbarer Zeitraum für einen Stichtag: Woche/Arbeitswoche/zwei Wochen ab Montag, Monat als ganze Wochen
 * (Montag vor dem Ersten bis Sonntag nach dem Letzten). `days` sind die angezeigten Tage in Reihenfolge.
 */
export function calendarPeriod(range: CalRange, anchor: string): { from: string; to: string; days: string[]; month: string | null } {
  let from: string;
  let to: string;
  if (range === 'month') {
    const first = `${anchor.slice(0, 7)}-01`;
    const last = addDayKeys(`${addDayKeys(first, 32).slice(0, 7)}-01`, -1);
    from = mondayOf(first);
    to = addDayKeys(mondayOf(last), 6);
  } else {
    from = mondayOf(anchor);
    to = addDayKeys(from, range === 'twoWeeks' ? 13 : 6);
  }
  const all = Array.from({ length: dayDiff(from, to) + 1 }, (_, i) => addDayKeys(from, i));
  const days = range === 'workweek' ? all.filter((d) => weekdayOf(d) < 5) : all;
  return { from, to: range === 'workweek' ? addDayKeys(from, 4) : to, days, month: range === 'month' ? anchor.slice(0, 7) : null };
}

/** Stichtag des vorherigen/nächsten Zeitraums */
export function shiftPeriod(range: CalRange, anchor: string, dir: -1 | 1): string {
  if (range === 'month') {
    const [y, m] = anchor.split('-').map(Number);
    const d = new Date(Date.UTC(y!, m! - 1 + dir, 1));
    return key(d);
  }
  return addDayKeys(mondayOf(anchor), dir * (range === 'twoWeeks' ? 14 : 7));
}

/* ---------- Einträge ---------- */

export interface CalDatesOf {
  startAt: string | null;
  startHasTime: boolean;
  dueAt: string | null;
  dueHasTime: boolean;
}

/** Ein Eintrag im Kalender. `kind` bestimmt, was Ziehen ändert. */
export interface CalEvent<C extends CalDatesOf = CalDatesOf> {
  card: C;
  kind: 'due' | 'start' | 'span';
  /** erster und letzter Kalendertag (gleich bei Einzelterminen) */
  startDay: string;
  endDay: string;
  /** Uhrzeit-Beginn bzw. -Ende (ISO) – nur bei Terminen mit Uhrzeit */
  startTime: string | null;
  endTime: string | null;
  /** im Zeitraster als Fläche an der Uhrzeit (sonst ganztägig/über Tage) */
  timed: boolean;
}

/** Einträge einer Karte nach der Wahl „Welche Termine“ */
export function cardEvents<C extends CalDatesOf>(card: C, dates: CalDates): CalEvent<C>[] {
  const due = card.dueAt
    ? ({ card, kind: 'due', startDay: calendarDate(card.dueAt, card.dueHasTime), endDay: calendarDate(card.dueAt, card.dueHasTime), startTime: card.dueHasTime ? card.dueAt : null, endTime: null, timed: card.dueHasTime } as CalEvent<C>)
    : null;
  const start = card.startAt
    ? ({ card, kind: 'start', startDay: calendarDate(card.startAt, card.startHasTime), endDay: calendarDate(card.startAt, card.startHasTime), startTime: card.startHasTime ? card.startAt : null, endTime: null, timed: card.startHasTime } as CalEvent<C>)
    : null;
  if (dates === 'due') return due ? [due] : [];
  if (dates === 'start') return start ? [start] : [];
  if (dates === 'both') return [start, due].filter((e): e is CalEvent<C> => !!e);
  // Zeitraum: beide Angaben → ein Eintrag; nur eine → Einzeltermin
  if (!start || !due) return start ? [start] : due ? [due] : [];
  if (start.startDay > due.startDay) return [start, due]; // ungültiger Zeitraum: nicht verschmelzen
  const sameDay = start.startDay === due.startDay;
  const timed = sameDay && card.startHasTime && card.dueHasTime;
  return [{ card, kind: 'span', startDay: start.startDay, endDay: due.startDay, startTime: start.startTime, endTime: due.startTime, timed }];
}

/** Einträge, die in [from, to] liegen (mit Überschneidung) */
export function calendarEvents<C extends CalDatesOf>(cards: C[], dates: CalDates, from: string, to: string): CalEvent<C>[] {
  const out: CalEvent<C>[] = [];
  for (const c of cards) for (const e of cardEvents(c, dates)) if (e.endDay >= from && e.startDay <= to) out.push(e);
  return out.sort((a, b) => (a.startDay < b.startDay ? -1 : a.startDay > b.startDay ? 1 : (a.startTime ?? '') < (b.startTime ?? '') ? -1 : 1));
}

/* ---------- Anordnung ---------- */

/**
 * Mehrtägige und ganztägige Einträge einer Woche in Zeilen legen (gierig, frühester Beginn zuerst):
 * `lane` ist die Zeile, `col`/`span` Beginn und Länge in Tagen innerhalb von `days`.
 */
export function layoutWeek<E extends { startDay: string; endDay: string }>(events: E[], days: string[]): { e: E; lane: number; col: number; span: number; cutStart: boolean; cutEnd: boolean }[] {
  const first = days[0]!;
  const last = days.at(-1)!;
  const idx = (d: string) => {
    // Arbeitswoche: Wochenende auf den nächsten bzw. vorigen sichtbaren Tag
    const i = days.indexOf(d);
    if (i >= 0) return i;
    return days.findIndex((x) => x > d);
  };
  const visible = events.filter((e) => e.endDay >= first && e.startDay <= last);
  const sorted = [...visible].sort((a, b) => (a.startDay < b.startDay ? -1 : a.startDay > b.startDay ? 1 : dayDiff(b.startDay, b.endDay) - dayDiff(a.startDay, a.endDay)));
  const lanes: number[] = []; // je Zeile: letzte belegte Spalte
  return sorted.flatMap((e) => {
    const col = e.startDay < first ? 0 : idx(e.startDay);
    let end = e.endDay > last ? days.length - 1 : days.indexOf(e.endDay);
    if (end < 0) end = days.findIndex((x) => x > e.endDay) - 1;
    if (col < 0 || end < col) return [];
    let lane = lanes.findIndex((l) => l < col);
    if (lane < 0) lane = lanes.push(-1) - 1;
    lanes[lane] = end;
    return [{ e, lane, col, span: end - col + 1, cutStart: e.startDay < first, cutEnd: e.endDay > last }];
  });
}

/** Minuten seit Mitternacht (lokal) */
export const minuteOfDay = (iso: string) => {
  const d = new Date(iso);
  return d.getHours() * 60 + d.getMinutes();
};

/**
 * Flächen im Zeitraster eines Tages: Beginn/Ende in Minuten (Mindestlänge 30), überlappende Einträge
 * nebeneinander (`slot` von `slots`).
 */
export function layoutDay<E extends { startTime: string | null; endTime: string | null }>(events: E[], minLen = 30): { e: E; top: number; end: number; slot: number; slots: number }[] {
  const items = events
    .filter((e) => e.startTime)
    .map((e) => {
      const top = minuteOfDay(e.startTime!);
      const end = e.endTime ? Math.max(top + minLen,
        localDateKey(new Date(e.endTime)) > localDateKey(new Date(e.startTime!)) ? 1440 : minuteOfDay(e.endTime)) : top + minLen;
      return { e, top, end: Math.min(end, 24 * 60), slot: 0, slots: 1 };
    })
    .sort((a, b) => a.top - b.top || b.end - a.end);
  // Gruppen überlappender Einträge; innerhalb einer Gruppe Spalten vergeben
  let group: typeof items = [];
  let groupEnd = -1;
  const close = () => {
    const cols: number[] = [];
    for (const it of group) {
      let s = cols.findIndex((c) => c <= it.top);
      if (s < 0) s = cols.push(0) - 1;
      cols[s] = it.end;
      it.slot = s;
    }
    for (const it of group) it.slots = cols.length;
    group = [];
  };
  for (const it of items) {
    if (it.top >= groupEnd && group.length) close();
    group.push(it);
    groupEnd = Math.max(groupEnd, it.end);
  }
  if (group.length) close();
  return items;
}

/* ---------- Umplanen ---------- */

export type CalPatch = Partial<{ startAt: string; startHasTime: boolean; dueAt: string; dueHasTime: boolean }>;

/** Zeitpunkt aus Kalendertag und Uhrzeit „HH:MM“ (lokal) */
export function atLocal(day: string, time: string): string {
  const [y, m, d] = day.split('-').map(Number);
  const [hh, mm] = time.split(':').map(Number);
  return new Date(y!, m! - 1, d!, hh!, mm!).toISOString();
}
const plusMinutes = (iso: string, min: number) => new Date(new Date(iso).getTime() + min * 60_000).toISOString();

/**
 * Neue Termine, wenn ein Eintrag auf einen Tag (optional mit Uhrzeit) gezogen wird. Gleiche Regeln wie im
 * Board-Kalender: Frist zieht einen späteren Start mit, Start zieht eine frühere Frist mit; ein Zeitraum
 * wandert als Ganzes (Dauer bleibt). Ohne Änderung → null.
 */
export function rescheduleEvent(card: CalDatesOf, kind: CalEvent['kind'], target: { day: string; time?: string | null }): CalPatch | null {
  const time = target.time ?? null;
  if (kind === 'due') {
    if (!card.dueAt) return null;
    const dueHasTime = time ? true : card.dueHasTime;
    const dueAt = time ? atLocal(target.day, time) : moveToDate(card.dueAt, card.dueHasTime, target.day);
    if (dueAt === card.dueAt && dueHasTime === card.dueHasTime) return null;
    return { dueAt, dueHasTime, ...shiftStartWithDue(card, dueAt, dueHasTime) };
  }
  if (kind === 'start') {
    if (!card.startAt) return null;
    const startHasTime = time ? true : card.startHasTime;
    const startAt = time ? atLocal(target.day, time) : moveToDate(card.startAt, card.startHasTime, target.day);
    if (startAt === card.startAt && startHasTime === card.startHasTime) return null;
    const patch: CalPatch = { startAt, startHasTime };
    if (card.dueAt && startAfterDue(startAt, startHasTime, card.dueAt, card.dueHasTime)) {
      const delta = dayDiff(calendarDate(card.startAt, card.startHasTime), target.day);
      let dueAt = moveToDate(card.dueAt, card.dueHasTime, addDayKeys(calendarDate(card.dueAt, card.dueHasTime), delta));
      if (startAfterDue(startAt, startHasTime, dueAt, card.dueHasTime)) dueAt = card.dueHasTime ? plusMinutes(startAt, 60) : allDayIso(target.day);
      patch.dueAt = dueAt;
      patch.dueHasTime = card.dueHasTime;
    }
    return patch;
  }
  // Zeitraum
  if (!card.startAt || !card.dueAt) return null;
  const startDay = calendarDate(card.startAt, card.startHasTime);
  if (time) {
    const newStart = atLocal(target.day, time);
    if (card.startHasTime && card.dueHasTime) {
      // Dauer bleibt
      const dur = new Date(card.dueAt).getTime() - new Date(card.startAt).getTime();
      const dueAt = new Date(new Date(newStart).getTime() + dur).toISOString();
      if (newStart === card.startAt) return null;
      return { startAt: newStart, startHasTime: true, dueAt, dueHasTime: true };
    }
    // Ganztägig auf eine Uhrzeit: Start dort, Ende so viele Tage später (eine Stunde, wenn derselbe Tag)
    const days = dayDiff(startDay, calendarDate(card.dueAt, card.dueHasTime));
    const dueAt = days > 0 ? (card.dueHasTime ? moveToDate(card.dueAt, true, addDayKeys(target.day, days)) : allDayIso(addDayKeys(target.day, days))) : plusMinutes(newStart, 60);
    return { startAt: newStart, startHasTime: true, dueAt, dueHasTime: days > 0 ? card.dueHasTime : true };
  }
  const delta = dayDiff(startDay, target.day);
  if (!delta) return null;
  return {
    startAt: moveToDate(card.startAt, card.startHasTime, target.day),
    startHasTime: card.startHasTime,
    dueAt: moveToDate(card.dueAt, card.dueHasTime, addDayKeys(calendarDate(card.dueAt, card.dueHasTime), delta)),
    dueHasTime: card.dueHasTime,
  };
}

/** Heute als Kalendertag (lokal) */
export const todayKey = (now = new Date()) => localDateKey(now);
